import { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, ArrowRight, Users, Building2, Check } from "lucide-react";
import { getDeletePreflight, listOwners, deleteOwnerWithTransfer, listOrgsByOwner } from "../services/adminApi";
import { useToast } from "./ToastProvider";

const T = {
  dark: "#0c1118", amber: "#f9ab14", bg: "#f4f6f8",
  surface: "#ffffff", border: "#e8ecf0", muted: "#6b7280",
};

const SEL = { width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, background: T.surface, fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" };
const LBL = { fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 4, display: "block" };
const BTN = { padding: "8px 18px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: "'DM Sans', sans-serif" };
const BTN_DARK = { ...BTN, background: T.dark, color: "#fff", border: "none" };
const BTN_RED  = { ...BTN, background: "#dc2626", color: "#fff", border: "none" };

function DestinationPicker({ row, onChange, owners, ownerOrgsCache, setOwnerOrgsCache }) {
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const destOwnerOrgs = row.destinationOwnerUserId ? ownerOrgsCache[row.destinationOwnerUserId] || [] : [];

  async function ensureOwnerOrgs(ownerId) {
    if (!ownerId || ownerOrgsCache[ownerId]) return;
    setLoadingOrgs(true);
    try {
      const { orgs } = await listOrgsByOwner(ownerId);
      setOwnerOrgsCache((prev) => ({ ...prev, [ownerId]: orgs || [] }));
    } catch {
      setOwnerOrgsCache((prev) => ({ ...prev, [ownerId]: [] }));
    } finally { setLoadingOrgs(false); }
  }

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, background: T.bg }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
        Source company: <b>{row.sourceOrgName}</b>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        <div>
          <label style={LBL}>Transfer Mode</label>
          <select style={SEL} value={row.mode} onChange={(e) => {
            const mode = e.target.value;
            onChange({ ...row, mode });
            if (mode === "merge" && row.destinationOwnerUserId) ensureOwnerOrgs(row.destinationOwnerUserId);
          }}>
            <option value="new">Create new company</option>
            <option value="merge">Merge into existing company</option>
          </select>
        </div>
        <div>
          <label style={LBL}>Destination Owner</label>
          <select style={SEL} value={row.destinationOwnerUserId} onChange={async (e) => {
            const destinationOwnerUserId = e.target.value;
            onChange({ ...row, destinationOwnerUserId, destinationOrgId: "" });
            if (row.mode === "merge" && destinationOwnerUserId) await ensureOwnerOrgs(destinationOwnerUserId);
          }}>
            <option value="">— Select owner —</option>
            {owners.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={LBL}>Destination Company (when merging)</label>
          <select
            style={{ ...SEL, opacity: (row.mode !== "merge" || !row.destinationOwnerUserId) ? 0.5 : 1 }}
            value={row.destinationOrgId || ""}
            disabled={row.mode !== "merge" || !row.destinationOwnerUserId}
            onFocus={() => { if (row.mode === "merge" && row.destinationOwnerUserId) ensureOwnerOrgs(row.destinationOwnerUserId); }}
            onChange={(e) => onChange({ ...row, destinationOrgId: e.target.value })}
          >
            <option value="">{loadingOrgs ? "Loading companies…" : "— Select company —"}</option>
            {row.mode === "merge" && destOwnerOrgs.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
        </div>
      </div>
      {row.mode === "new" && (
        <div style={{ marginTop: 10, fontSize: 12, color: T.muted }}>
          A new company will be created under the selected owner as "{row.sourceOrgName} (migrated)".
        </div>
      )}
    </div>
  );
}

export default function DeleteOwnerWizard({ ownerId, open, onClose, onDone }) {
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [pre, setPre] = useState(null);
  const [owners, setOwners] = useState([]);
  const ownersById = useMemo(() => Object.fromEntries((owners || []).map((o) => [o.id, o])), [owners]);
  const [ownerOrgsCache, setOwnerOrgsCache] = useState({});
  const [rows, setRows] = useState([]);
  const [suspendMoved, setSuspendMoved] = useState(true);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [p, o] = await Promise.all([getDeletePreflight(ownerId), listOwners()]);
        if (!mounted) return;
        setPre(p);
        setOwners(o.owners || []);
        setRows((p.ownedOrgs || []).map((org) => ({ sourceOrgId: org.id, sourceOrgName: org.name, mode: "new", destinationOwnerUserId: "", destinationOrgId: "" })));
        setStep(1); setConfirmText(""); setSuspendMoved(true);
      } catch (e) {
        toast.error(e?.message || "Failed to prepare deletion");
        onClose?.();
      } finally { setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open, ownerId, toast, onClose]);

  if (!open) return null;

  const summary = pre?.summary || { orgs: 0, members: 0, projects: 0 };

  function updateRow(idx, next) { setRows((prev) => prev.map((r, i) => (i === idx ? next : r))); }

  function canContinueFromStep2() {
    if (!rows.length) return false;
    for (const r of rows) {
      if (!r.destinationOwnerUserId) return false;
      if (r.mode === "merge" && !r.destinationOrgId) return false;
    }
    return true;
  }

  async function submit() {
    setLoading(true);
    try {
      await deleteOwnerWithTransfer(ownerId, {
        mappings: rows.map((r) => ({ sourceOrgId: r.sourceOrgId, mode: r.mode, destinationOwnerUserId: r.destinationOwnerUserId, destinationOrgId: r.mode === "merge" ? r.destinationOrgId : undefined })),
        suspendMovedUsers: suspendMoved,
      });
      toast.success("Owner deleted and companies migrated.");
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    } finally { setLoading(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} onClick={onClose} />
      <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: "96vw", maxWidth: 820, background: T.surface, borderRadius: 16, boxShadow: "0 8px 60px rgba(0,0,0,0.22)" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>Delete Company Owner</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>Safely migrate companies, members, and projects before deletion.</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 8, borderRadius: 8 }}><X size={18} /></button>
        </div>

        {/* Stepper */}
        <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
          <StepDot active={step === 1} done={step > 1} label="Review" />
          <ArrowRight size={16} color={T.muted} />
          <StepDot active={step === 2} done={step > 2} label="Mappings" />
          <ArrowRight size={16} color={T.muted} />
          <StepDot active={step === 3} done={false} label="Confirm" />
        </div>

        {/* Body */}
        <div style={{ padding: "0 24px 16px", maxHeight: "62vh", overflowY: "auto" }}>
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ borderRadius: 10, border: "1px solid #fde68a", background: "#fffbeb", padding: 16, display: "flex", gap: 12 }}>
                <AlertTriangle size={18} color="#d97706" style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: "#92400e" }}>
                  Deleting an owner will migrate their companies. Staff & members will be <b>{suspendMoved ? "suspended" : "not suspended"}</b> after transfer.
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                <SummaryCard icon={Building2} label="Companies" value={summary.orgs} />
                <SummaryCard icon={Users} label="Members (excl. owner)" value={summary.members} />
                <SummaryCard icon={Check} label="Projects" value={summary.projects} />
              </div>

              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 12 }}>Companies to migrate</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(pre?.ownedOrgs || []).map((o) => (
                    <div key={o.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                      <span style={{ fontWeight: 500 }}>{o.name}</span>
                      <span style={{ color: T.muted }}>{o.members.length} members • {o.projects.length} projects</span>
                    </div>
                  ))}
                  {!pre?.ownedOrgs?.length && <div style={{ fontSize: 13, color: T.muted }}>No companies owned. You can delete directly from drawer.</div>}
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" style={{ width: 15, height: 15 }} checked={suspendMoved} onChange={(e) => setSuspendMoved(e.target.checked)} />
                Suspend moved users after transfer (recommended)
              </label>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {rows.map((r, idx) => (
                <DestinationPicker key={r.sourceOrgId} row={r} onChange={(next) => updateRow(idx, next)} owners={owners} ownersById={ownersById} ownerOrgsCache={ownerOrgsCache} setOwnerOrgsCache={setOwnerOrgsCache} />
              ))}
              {!rows.length && <div style={{ fontSize: 13, color: T.muted }}>No companies to transfer — go to the final step to delete the owner.</div>}
            </div>
          )}

          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 16 }}>
                <div style={{ fontWeight: 700, marginBottom: 10 }}>Summary</div>
                <ul style={{ paddingLeft: 20, fontSize: 13, display: "flex", flexDirection: "column", gap: 6, margin: 0 }}>
                  {rows.map((r) => {
                    const ownerLabel = ownersById[r.destinationOwnerUserId]?.label || "—";
                    return (
                      <li key={r.sourceOrgId}>
                        <b>{r.sourceOrgName}</b> → {r.mode === "merge" ? (
                          <span>Merge into <b>{ownersById[r.destinationOwnerUserId]?.orgs?.find((o) => o.id === r.destinationOrgId)?.name || "Company"}</b> ({ownerLabel})</span>
                        ) : (
                          <span>Create new company under <b>{ownerLabel}</b></span>
                        )}
                      </li>
                    );
                  })}
                  {!rows.length && <li>No companies owned — owner will be deleted immediately.</li>}
                </ul>
              </div>

              <div style={{ borderRadius: 10, border: "1px solid #fca5a5", background: "#fef2f2", padding: 16, fontSize: 13, color: "#7f1d1d" }}>
                Type <b>DELETE OWNER</b> to confirm this irreversible action.
              </div>
              <input
                style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, boxSizing: "border-box", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="DELETE OWNER"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, background: T.bg, borderRadius: "0 0 16px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button style={BTN} onClick={onClose}>Cancel</button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 1 && (
              <button style={{ ...BTN, opacity: loading ? 0.5 : 1 }} onClick={() => setStep(step - 1)} disabled={loading}>Back</button>
            )}
            {step < 3 && (
              <button
                style={{ ...BTN_DARK, opacity: (loading || (step === 2 && !canContinueFromStep2())) ? 0.5 : 1 }}
                onClick={() => setStep(step + 1)}
                disabled={loading || (step === 2 && !canContinueFromStep2())}
              >Continue</button>
            )}
            {step === 3 && (
              <button
                style={{ ...BTN_RED, opacity: (loading || confirmText.trim() !== "DELETE OWNER") ? 0.5 : 1 }}
                disabled={loading || confirmText.trim() !== "DELETE OWNER"}
                onClick={submit}
              >{loading ? "Deleting…" : "Delete Owner & Transfer"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepDot({ active, done, label }) {
  const dotBg = active ? T.dark : done ? "#16a34a" : T.border;
  const dotColor = active || done ? "#fff" : T.muted;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, background: dotBg, color: dotColor }}>
        {done ? <Check size={13} /> : label[0]}
      </div>
      <span style={{ fontSize: 13, fontWeight: active ? 700 : 400, color: active ? T.dark : T.muted }}>{label}</span>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value }) {
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, background: T.surface, display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: T.dark, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: T.muted }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      </div>
    </div>
  );
}
